import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from './Button.tsx'

/**
 * The one way to ask "are you sure".
 *
 * `const ok = await confirm({ ... })` from anywhere under the provider; the
 * question is drawn as a modal and the promise answers when the person does.
 * One ingredient, so every destructive action in the product asks the same
 * way: a title that names the thing, a sentence on what happens, and the
 * verb on a red button when the answer cannot be undone.
 */
export interface ConfirmRequest {
  readonly title: string
  readonly message?: ReactNode
  /** The verb on the button: "Remover", "Apagar", "Parar". */
  readonly confirmLabel?: string
  readonly cancelLabel?: string
  /** `danger` for what cannot be undone; the default is a plain question. */
  readonly tone?: 'brand' | 'danger'
}

type Ask = (request: ConfirmRequest) => Promise<boolean>

const ConfirmContext = createContext<Ask | undefined>(undefined)

interface Pending {
  readonly request: ConfirmRequest
  readonly resolve: (answer: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | undefined>()

  const ask = useCallback<Ask>(
    (request) =>
      new Promise<boolean>((resolve) => {
        setPending({ request, resolve })
      }),
    [],
  )

  const answer = (value: boolean) => {
    pending?.resolve(value)
    setPending(undefined)
  }

  const api = useMemo(() => ask, [ask])

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {pending !== undefined && <ConfirmDialog request={pending.request} onAnswer={answer} />}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext)
  if (ask === undefined) throw new Error('useConfirm needs a ConfirmProvider above it')
  return ask
}

function ConfirmDialog({
  request,
  onAnswer,
}: {
  request: ConfirmRequest
  onAnswer: (answer: boolean) => void
}) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const tone = request.tone ?? 'brand'

  // Focus lands on the verb, Escape is "no": what a keyboard expects of a
  // question, and what stops a stray Enter from answering it by accident.
  useEffect(() => {
    confirmRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onAnswer(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onAnswer])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onAnswer(false)
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="bg-background-raised rounded-base shadow-raised border-stroke-weak flex w-full max-w-[28rem] flex-col gap-4 border p-5"
      >
        <div className="flex flex-col gap-1">
          <h2 id="confirm-title" className="text-tPSemi text-text-strong">
            {request.title}
          </h2>
          {request.message !== undefined && (
            <p className="text-tP text-text-weak">{request.message}</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" tone="neutral" size="small" onClick={() => onAnswer(false)}>
            {request.cancelLabel ?? 'Cancelar'}
          </Button>
          <Button
            ref={confirmRef}
            variant="primary"
            tone={tone}
            size="small"
            onClick={() => onAnswer(true)}
          >
            {request.confirmLabel ?? 'Confirmar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
